(() => {
  'use strict';

  const ready = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  };

  ready(() => {
    const modal = document.getElementById('WkAppExperience');
    if (!modal) return;

    const body = document.body;
    const panel = modal.querySelector('.wk-app-experience__panel');
    const closeTargets = modal.querySelectorAll('[data-wk-app-close]');
    const navButtons = Array.from(modal.querySelectorAll('[data-wk-app-go]'));
    const slides = Array.from(modal.querySelectorAll('[data-wk-app-slide]'));
    const prevButton = modal.querySelector('[data-wk-app-prev]');
    const nextButton = modal.querySelector('[data-wk-app-next]');
    const currentLabel = modal.querySelector('[data-wk-app-current]');
    const progress = modal.querySelector('[data-wk-app-progress]');
    const viewport = modal.querySelector('[data-wk-app-viewport]');
    const rail = modal.querySelector('.wk-app-experience__rail');
    let currentIndex = 0;
    let previouslyFocused = null;
    let swipeStartX = 0;
    let swipeStartY = 0;
    let swipePointerId = null;
    let suppressClickUntil = 0;

    const clampIndex = (index) => {
      const total = slides.length;
      if (!total) return 0;
      return (index + total) % total;
    };

    const showSlide = (index, focusTab = false) => {
      currentIndex = clampIndex(index);

      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === currentIndex;
        slide.classList.toggle('is-active', active);
        slide.hidden = !active;
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });

      navButtons.forEach((button, buttonIndex) => {
        const active = buttonIndex === currentIndex;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', active ? 'true' : 'false');
        button.setAttribute('tabindex', active ? '0' : '-1');
        if (active) {
          if (rail) {
            const targetLeft = button.offsetLeft - ((rail.clientWidth - button.offsetWidth) / 2);
            rail.scrollTo({ left: Math.max(0, targetLeft), behavior: 'smooth' });
          }
          if (focusTab) button.focus({ preventScroll: true });
        }
      });

      if (currentLabel) currentLabel.textContent = String(currentIndex + 1).padStart(2, '0');
      if (progress) {
        const percentage = ((currentIndex + 1) / Math.max(slides.length, 1)) * 100;
        progress.style.setProperty('--wk-app-progress', `${percentage}%`);
      }

      if (viewport) viewport.scrollTop = 0;
    };

    const focusableElements = () => Array.from(
      panel.querySelectorAll(
        'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => !element.hidden && element.offsetParent !== null);

    const open = (trigger = null, startIndex = 0) => {
      previouslyFocused = trigger instanceof HTMLElement ? trigger : document.activeElement;
      showSlide(Number(startIndex) || 0);
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      body.classList.add('wk-app-open');

      window.requestAnimationFrame(() => {
        modal.classList.add('is-visible');
        const closeButton = modal.querySelector('.wk-app-experience__close');
        if (closeButton instanceof HTMLElement) closeButton.focus({ preventScroll: true });
      });
    };

    const close = () => {
      modal.classList.remove('is-visible');
      modal.setAttribute('aria-hidden', 'true');
      body.classList.remove('wk-app-open');

      window.setTimeout(() => {
        modal.hidden = true;
        if (previouslyFocused instanceof HTMLElement) {
          previouslyFocused.focus({ preventScroll: true });
        }
      }, 230);
    };

    window.WKAppExperience = { open, close, showSlide };

    document.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      const opener = target.closest('[data-wk-app-open]');
      if (!opener) return;
      event.preventDefault();
      const startIndex = opener.getAttribute('data-wk-app-open') || 0;
      open(opener, startIndex);
    });

    closeTargets.forEach((target) => target.addEventListener('click', close));
    navButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const targetIndex = Number(button.dataset.wkAppGo) || 0;
        showSlide(targetIndex);
      });
      button.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          event.preventDefault();
          showSlide(currentIndex + 1, true);
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          event.preventDefault();
          showSlide(currentIndex - 1, true);
        } else if (event.key === 'Home') {
          event.preventDefault();
          showSlide(0, true);
        } else if (event.key === 'End') {
          event.preventDefault();
          showSlide(slides.length - 1, true);
        }
      });
    });

    if (prevButton) prevButton.addEventListener('click', () => showSlide(currentIndex - 1));
    if (nextButton) nextButton.addEventListener('click', () => showSlide(currentIndex + 1));

    const canStartSwipe = (target) => {
      if (!(target instanceof Element)) return true;
      return !target.closest('a, button, input, select, textarea, label');
    };

    const finishSwipe = (endX, endY) => {
      if (!swipeStartX && !swipeStartY) return;

      const distanceX = endX - swipeStartX;
      const distanceY = endY - swipeStartY;
      swipeStartX = 0;
      swipeStartY = 0;

      if (Math.abs(distanceX) < 52 || Math.abs(distanceX) <= Math.abs(distanceY) * 1.25) return;

      suppressClickUntil = Date.now() + 350;
      showSlide(distanceX < 0 ? currentIndex + 1 : currentIndex - 1);
    };

    if (viewport && 'PointerEvent' in window) {
      viewport.addEventListener('pointerdown', (event) => {
        if (!event.isPrimary || event.button !== 0 || !canStartSwipe(event.target)) return;
        swipePointerId = event.pointerId;
        swipeStartX = event.clientX;
        swipeStartY = event.clientY;
      });

      viewport.addEventListener('pointerup', (event) => {
        if (event.pointerId !== swipePointerId) return;
        swipePointerId = null;
        finishSwipe(event.clientX, event.clientY);
      });

      viewport.addEventListener('pointercancel', (event) => {
        if (event.pointerId !== swipePointerId) return;
        swipePointerId = null;
        swipeStartX = 0;
        swipeStartY = 0;
      });
    } else if (viewport) {
      viewport.addEventListener('touchstart', (event) => {
        if (event.touches.length !== 1 || !canStartSwipe(event.target)) return;
        swipeStartX = event.touches[0].clientX;
        swipeStartY = event.touches[0].clientY;
      }, { passive: true });

      viewport.addEventListener('touchend', (event) => {
        if (!event.changedTouches.length) return;
        finishSwipe(event.changedTouches[0].clientX, event.changedTouches[0].clientY);
      }, { passive: true });

      viewport.addEventListener('touchcancel', () => {
        swipeStartX = 0;
        swipeStartY = 0;
      }, { passive: true });
    }

    if (viewport) {
      viewport.addEventListener('click', (event) => {
        if (Date.now() >= suppressClickUntil) return;
        event.preventDefault();
        event.stopPropagation();
      }, true);
    }

    modal.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = focusableElements();
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    showSlide(0);
  });
})();
